import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, FlaskConical, Info, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { apiClient } from '../../../api';

interface HarnessData {
  leakage: { passed: boolean; violations: { surface: string; field: string }[]; note: string };
  cv: { auc_stratified: number; auc_grouped: number; gap: number; warning: boolean; note: string } | null;
  stability: {
    resamples: number;
    exclusion_threshold: number;
    auc: { point: number; ci: { low: number; high: number } };
    recall_in_scope: { point: number | null; ci: { low: number; high: number } };
    accuracy: { point: number | null; ci: { low: number; high: number } };
    false_exclusions: number;
    false_inclusions: number;
    note: string;
  } | null;
  canaries: { passed: boolean; results: { match: string; expected: string; found: boolean; verdict: string | null; verdict_source: string | null; passed: boolean | null }[]; note: string };
  gate_recomputation: { active_gates: number; measured: number; oldest_measurement_hours: number | null; fresh: boolean; note: string };
  calibration: {
    sample_size: number;
    model: string;
    classifier_version: string;
    completed_at: string;
    summary: Record<string, { mean: number; spread: number; runs: number[] } | null> & { acceptance_threshold?: number | null; note?: string };
  } | null;
  headline: string;
}

interface RoundRow { id: number; seed: number; scope_note: string; status: string; created_at: string; review_count: number; reviewer_count: number; sample: { eai_nbr: number }[] }

interface RoundItem { eai_nbr: number; band: number; name: string | null; short_description: string | null; app_dsc: string | null; verdict: string | null; note: string | null }

interface RoundDetail { id: number; scope_note: string; reviewer: string; total: number; reviewed: number; items: RoundItem[]; note: string }

interface RoundReport {
  id: number;
  sample_size: number;
  reviewers: { reviewer: string; reviewed: number; unsure: number; engine_comparable: number; engine_agreement: number | null; potential_false_exclusions: number; per_band: Record<string, { reviewed: number; migrates: number; does_not_migrate: number; unsure: number }> }[];
  human_vs_human: { pairs: { reviewers: string[]; overlap: number; agreement: number | null; kappa: number | null }[]; note: string };
  note: string;
}

const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);

export default function ValidationLab({ refreshKey }: { refreshKey: number }) {
  const [harness, setHarness] = useState<HarnessData | null>(null);
  const [rounds, setRounds] = useState<RoundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [calRunId, setCalRunId] = useState<number | null>(null);
  const [calStatus, setCalStatus] = useState<string | null>(null);
  const [round, setRound] = useState<RoundDetail | null>(null);
  const [report, setReport] = useState<RoundReport | null>(null);
  const [note, setNote] = useState('');

  const loadOverview = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/api/cloud-portfolio/validation/harness'),
      apiClient.get('/api/cloud-portfolio/validation/rounds'),
    ])
      .then(([h, r]) => { setHarness(h.data); setRounds(r.data.items || []); setError(null); })
      .catch((e) => setError(e?.response?.data?.error || e.message || 'Failed to load the validation harness'))
      .finally(() => setLoading(false));
  };

  useEffect(loadOverview, [refreshKey]);

  const runCalibration = async () => {
    setBusy('calibration');
    try {
      const r = await apiClient.post('/api/cloud-portfolio/validation/calibration');
      setCalRunId(r.data.id);
      setCalStatus(`submitted — batch ${r.data.provider_batch_id || r.data.id}, ${r.data.requested} requests (${r.data.sample_size} apps × ${r.data.runs} runs)`);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Calibration submit failed');
    } finally {
      setBusy(null);
    }
  };

  const refreshCalibration = async () => {
    if (!calRunId) return;
    setBusy('calibration');
    try {
      const r = await apiClient.post(`/api/cloud-portfolio/validation/calibration/${calRunId}/refresh`);
      if (r.data.status === 'ended') {
        setCalStatus(null);
        setCalRunId(null);
        loadOverview();
      } else {
        setCalStatus(`provider status: ${r.data.provider_status || r.data.status}${r.data.request_counts ? ` — ${JSON.stringify(r.data.request_counts)}` : ''}`);
      }
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Calibration refresh failed');
    } finally {
      setBusy(null);
    }
  };

  const newRound = async () => {
    setBusy('round');
    try {
      await apiClient.post('/api/cloud-portfolio/validation/rounds');
      loadOverview();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Round creation failed');
    } finally {
      setBusy(null);
    }
  };

  const openRound = async (id: number) => {
    setBusy(`open-${id}`);
    try {
      const r = await apiClient.get(`/api/cloud-portfolio/validation/rounds/${id}`);
      setRound(r.data);
      setReport(null);
      setNote('');
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to open the round');
    } finally {
      setBusy(null);
    }
  };

  const openReport = async (id: number) => {
    setBusy(`report-${id}`);
    try {
      const r = await apiClient.get(`/api/cloud-portfolio/validation/rounds/${id}/report`);
      setReport(r.data);
      setRound(null);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load the report');
    } finally {
      setBusy(null);
    }
  };

  const currentItem = round ? round.items.find((i) => !i.verdict) || null : null;

  const verdict = async (v: string) => {
    if (!round || !currentItem) return;
    setBusy('verdict');
    try {
      await apiClient.post(`/api/cloud-portfolio/validation/rounds/${round.id}/verdicts`, {
        eai_nbr: currentItem.eai_nbr,
        verdict: v,
        note: note || null,
      });
      setRound({
        ...round,
        reviewed: round.reviewed + 1,
        items: round.items.map((i) => (i.eai_nbr === currentItem.eai_nbr ? { ...i, verdict: v, note } : i)),
      });
      setNote('');
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to record the verdict');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading validation harness…</p>;

  const passChip = (ok: boolean | null, labels: [string, string] = ['PASS', 'FAIL']) => (
    ok == null
      ? <span className="text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-px">N/A</span>
      : ok
        ? <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-px"><CheckCircle2 className="w-3 h-3" />{labels[0]}</span>
        : <span className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-px"><XCircle className="w-3 h-3" />{labels[1]}</span>
  );

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {harness && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-super-dark-purple">Validation harness — the engine's claims, continuously tested</span>
          </div>
          <p className="text-xs text-gray-500 mb-3">{harness.headline}</p>

          <div className="space-y-3">
            <div className="flex items-start gap-3 text-xs">
              <div className="w-36 flex-shrink-0 pt-0.5 font-semibold text-gray-700">Leakage audit</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {passChip(harness.leakage.passed)}
                  <span className="text-gray-600">{harness.leakage.violations.length === 0 ? 'No post-interview field reaches any feature surface.' : harness.leakage.violations.map((v) => `${v.field} → ${v.surface}`).join('; ')}</span>
                </div>
              </div>
            </div>

            {harness.cv && (
              <div className="flex items-start gap-3 text-xs">
                <div className="w-36 flex-shrink-0 pt-0.5 font-semibold text-gray-700">Grouped CV</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {harness.cv.warning
                      ? <span className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-px"><AlertTriangle className="w-3 h-3" />FAMILY-LEAKAGE WARNING</span>
                      : passChip(true, ['OK', ''])}
                    <span className="text-gray-600">stratified {harness.cv.auc_stratified} · grouped {harness.cv.auc_grouped} · gap {harness.cv.gap}</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">{harness.cv.note}</p>
                </div>
              </div>
            )}

            {harness.stability && (
              <div className="flex items-start gap-3 text-xs">
                <div className="w-36 flex-shrink-0 pt-0.5 font-semibold text-gray-700">Bootstrap stability</div>
                <div className="flex-1">
                  <p className="text-gray-700 font-semibold">
                    Recall on in-scope {pct(harness.stability.recall_in_scope.point)} (95% CI {pct(harness.stability.recall_in_scope.ci.low)}–{pct(harness.stability.recall_in_scope.ci.high)}) at exclusion boundary p ≤ {harness.stability.exclusion_threshold}
                  </p>
                  <p className="text-gray-600 mt-0.5">
                    AUC {harness.stability.auc.point} ({harness.stability.auc.ci.low}–{harness.stability.auc.ci.high}) · accuracy {pct(harness.stability.accuracy.point)} · {harness.stability.false_exclusions} false exclusions vs {harness.stability.false_inclusions} false inclusions · {harness.stability.resamples} resamples
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{harness.stability.note}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 text-xs">
              <div className="w-36 flex-shrink-0 pt-0.5 font-semibold text-gray-700">Canary set</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">{passChip(harness.canaries.passed)}<span className="text-gray-500">{harness.canaries.note}</span></div>
                {harness.canaries.results.map((c) => (
                  <p key={c.match} className="text-[11px] text-gray-600">
                    {c.match}: {c.found ? `${c.verdict} (${c.verdict_source})` : 'not in extract'} {c.found && passChip(c.passed)}
                  </p>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 text-xs">
              <div className="w-36 flex-shrink-0 pt-0.5 font-semibold text-gray-700">Gate recomputation</div>
              <div className="flex-1 flex items-center gap-2">
                {passChip(harness.gate_recomputation.fresh, ['FRESH', 'STALE'])}
                <span className="text-gray-600">
                  {harness.gate_recomputation.measured} of {harness.gate_recomputation.active_gates} active gates measured · oldest {harness.gate_recomputation.oldest_measurement_hours ?? '—'}h ago · {harness.gate_recomputation.note}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <FlaskConical className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Language-layer calibration (SR-4)</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">Pinned prompt and model, three passes over the same seeded 100-app labelled sample. The acceptance threshold comes from this run — never from the single conversational estimate.</p>
        {harness?.calibration ? (
          <div className="text-xs space-y-1">
            <p className="text-gray-700 font-semibold">
              Baseline — {harness.calibration.model} · prompt v{harness.calibration.classifier_version} · {harness.calibration.sample_size} apps · completed {new Date(harness.calibration.completed_at).toISOString().slice(0, 10)}
            </p>
            {['accuracy', 'precision_migrates', 'recall_migrates', 'kappa'].map((f) => {
              const s = harness.calibration!.summary[f] as { mean: number; spread: number; runs: number[] } | null;
              return s ? (
                <p key={f} className="text-gray-600">{f}: mean {s.mean} · spread {s.spread} · runs [{s.runs.join(', ')}]</p>
              ) : null;
            })}
            <p className="text-gray-700 font-semibold">Acceptance threshold (accuracy): {harness.calibration.summary.acceptance_threshold ?? '—'}</p>
          </div>
        ) : (
          <p className="text-xs text-gray-500 mb-2">No calibration baseline yet — the 0.753 figure in the ticket is an unvalidated single conversational run, not a benchmark.</p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={runCalibration}
            disabled={busy === 'calibration' || !!calRunId}
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold disabled:opacity-50"
          >
            {busy === 'calibration' && !calRunId ? 'Submitting…' : 'Run calibration (100 apps × 3)'}
          </button>
          {calRunId && (
            <button
              onClick={refreshCalibration}
              disabled={busy === 'calibration'}
              className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === 'calibration' ? 'Checking…' : 'Check batch status'}
            </button>
          )}
          {calStatus && <span className="text-[11px] text-gray-500">{calStatus}</span>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-super-dark-purple">Human validation — the one that matters</span>
          <button
            onClick={newRound}
            disabled={busy === 'round'}
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold disabled:opacity-50"
          >
            {busy === 'round' ? 'Sampling…' : 'New blind round (30 × Band 5 + 20 × Band 2)'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Blind-reviewed by Steve or T — the first real generalisation estimate outside NJ, and the human-versus-human agreement that is the true ceiling.</p>
        {rounds.length === 0 ? (
          <p className="text-xs text-gray-400">No rounds yet.</p>
        ) : (
          <div className="space-y-1.5">
            {rounds.map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-xs border border-gray-200 rounded-lg px-3 py-2">
                <span className="font-semibold text-gray-700">Round {r.id}</span>
                <span className="text-gray-500 truncate flex-1 min-w-0">{r.scope_note}</span>
                <span className="text-gray-500 whitespace-nowrap">{r.review_count} verdicts · {r.reviewer_count} reviewer{r.reviewer_count === 1 ? '' : 's'}</span>
                <button
                  onClick={() => openRound(r.id)}
                  disabled={busy !== null}
                  className="flex items-center gap-1 text-amber-700 font-semibold hover:underline whitespace-nowrap disabled:opacity-50 disabled:cursor-wait"
                >
                  {busy === `open-${r.id}` && <Loader2 className="w-3 h-3 animate-spin" />}
                  {busy === `open-${r.id}` ? 'opening…' : 'review'}
                </button>
                <button
                  onClick={() => openReport(r.id)}
                  disabled={busy !== null}
                  className="flex items-center gap-1 text-amber-700 font-semibold hover:underline whitespace-nowrap disabled:opacity-50 disabled:cursor-wait"
                >
                  {busy === `report-${r.id}` && <Loader2 className="w-3 h-3 animate-spin" />}
                  {busy === `report-${r.id}` ? 'loading…' : 'report'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {round && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-super-dark-purple mb-1">Blind review · Round {round.id} — {round.reviewed} of {round.total} done</p>
          <p className="text-[11px] text-gray-400 mb-3">{round.note}</p>
          {currentItem ? (
            <div className="space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-bold text-super-dark-purple">{currentItem.name || `EAI ${currentItem.eai_nbr}`}</p>
                {currentItem.short_description && <p className="text-xs text-gray-600 mt-1.5">{currentItem.short_description}</p>}
                {currentItem.app_dsc && currentItem.app_dsc !== currentItem.short_description && (
                  <p className="text-xs text-gray-500 mt-1.5">{currentItem.app_dsc}</p>
                )}
              </div>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note"
                className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2"
              />
              <div className="flex items-center gap-2">
                <button onClick={() => verdict('MIGRATES')} disabled={busy === 'verdict'} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50">Migrates</button>
                <button onClick={() => verdict('DOES_NOT_MIGRATE')} disabled={busy === 'verdict'} className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold disabled:opacity-50">Does not migrate</button>
                <button onClick={() => verdict('UNSURE')} disabled={busy === 'verdict'} className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">Unsure</button>
                <button onClick={() => setRound(null)} className="ml-auto text-[11px] text-gray-500 hover:underline">close</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Every application in this round has your verdict. Open the report to see the result.
              <button onClick={() => openReport(round.id)} disabled={busy !== null} className="font-semibold underline disabled:opacity-50 disabled:cursor-wait">
                {busy === `report-${round.id}` ? 'loading…' : 'report'}
              </button>
              <button onClick={() => setRound(null)} className="ml-auto hover:underline">close</button>
            </div>
          )}
        </div>
      )}

      {report && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-super-dark-purple">Round {report.id} report · {report.sample_size} applications</p>
            <button onClick={() => setReport(null)} className="text-[11px] text-gray-500 hover:underline">close</button>
          </div>
          <p className="text-[11px] text-gray-400 mb-3">{report.note}</p>
          {report.reviewers.length === 0 && <p className="text-xs text-gray-400">No verdicts recorded yet.</p>}
          {report.reviewers.map((rv) => (
            <div key={rv.reviewer} className="text-xs border border-gray-200 rounded-lg p-3 mb-2">
              <p className="font-semibold text-gray-700">{rv.reviewer} — {rv.reviewed} reviewed ({rv.unsure} unsure)</p>
              <p className="text-gray-600 mt-1">
                Engine agreement {pct(rv.engine_agreement)} on {rv.engine_comparable} comparable ·{' '}
                <span className={rv.potential_false_exclusions > 0 ? 'font-bold text-amber-700' : ''}>
                  {rv.potential_false_exclusions} engine exclusion{rv.potential_false_exclusions === 1 ? '' : 's'} called MIGRATES
                </span>
              </p>
              {[5, 2].map((b) => (
                <p key={b} className="text-[11px] text-gray-500 mt-0.5">
                  Band {b}: {rv.per_band[b]?.reviewed || 0} reviewed — {rv.per_band[b]?.migrates || 0} migrates · {rv.per_band[b]?.does_not_migrate || 0} does not · {rv.per_band[b]?.unsure || 0} unsure
                </p>
              ))}
            </div>
          ))}
          {report.human_vs_human.pairs.length > 0 && (
            <div className="text-xs border border-gray-200 rounded-lg p-3">
              <p className="font-semibold text-gray-700 mb-1">Human vs human — {report.human_vs_human.note}</p>
              {report.human_vs_human.pairs.map((p) => (
                <p key={p.reviewers.join('|')} className="text-gray-600">
                  {p.reviewers.join(' vs ')}: agreement {pct(p.agreement)} on {p.overlap} shared · kappa {p.kappa ?? '—'}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
